// Pure helpers behind LocationPerformanceTab. Kept side-effect-free so the
// reconciliation rules can be unit-tested without React/network plumbing.

export type GrowthTone = 'success' | 'danger' | 'neutral';

export interface GrowthBadge {
  label: string;
  tone: GrowthTone;
}

/**
 * Formats the dashboard summary's growth_pct field for the Performance tab.
 *
 * Mirrors the sign convention used by /dashboard/summary: positive => "up"
 * (success), negative => "down" (danger), zero/missing => neutral dash.
 */
export function growthBadge(growthPct: number | null | undefined): GrowthBadge {
  if (growthPct === null || growthPct === undefined || !Number.isFinite(growthPct) || growthPct === 0) {
    return { label: '—', tone: 'neutral' };
  }
  const sign = growthPct > 0 ? '+' : '';
  const tone: GrowthTone = growthPct > 0 ? 'success' : 'danger';
  return { label: `${sign}${growthPct.toFixed(1)}%`, tone };
}

/**
 * Returns the peak hour (0–23, the store's clock) from a sales-wave series, or
 * null when the series has no revenue. Pure so the same logic can be applied
 * to both today's series and the comparison series.
 */
export function peakHour(series: ReadonlyArray<{ hour: number; revenue: number }>): number | null {
  let best: { hour: number; revenue: number } | null = null;
  for (const row of series) {
    const rev = Number(row.revenue) || 0;
    if (rev <= 0) continue;
    if (!best || rev > best.revenue) best = { hour: row.hour, revenue: rev };
  }
  return best ? best.hour : null;
}

/**
 * Sum of today's sales-wave series (in paise). The Performance tab must
 * reconcile with the header KPI: this sum equals the Today's Sales card.
 */
export function reconciledTodayPaise(
  series: ReadonlyArray<{ hour: number; revenue: number }>,
): number {
  let total = 0;
  for (const row of series) {
    const v = Number(row.revenue);
    if (Number.isFinite(v)) total += v;
  }
  return total;
}

/**
 * Formats an hour-of-day (0–23) as a short 12-hour label (e.g. 14 -> "2 PM").
 */
export function formatHour(hour: number | null | undefined): string {
  if (hour === null || hour === undefined || !Number.isFinite(hour)) return '—';
  const h = Math.trunc(hour);
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  if (h < 12) return `${h} AM`;
  return `${h - 12} PM`;
}
